import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { queryClient } from "../../lib/queryClient";
import { mutateUser, useUser } from "../../hooks/useUser";
import {
  useAccountSwitchListClassesList,
  useAccountSwitchSelectClassCreate,
} from "../../api-client/account-switch/account-switch";
import type { ClassNameListResponse } from "../../api-client/models/classNameListResponse";

interface TAHeaderProps {
  menuOpen: boolean;
  onClose?: () => void;
}

export default function TAHeader({ menuOpen, onClose }: TAHeaderProps) {
  const user = useUser();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [className, setClassName] = useState(user.className.id);
  const [justSaved, setJustSaved] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: availableClasses } = useAccountSwitchListClassesList();
  const { mutateAsync: selectClassMutation } = useAccountSwitchSelectClassCreate();

  const [prevClassNameId, setPrevClassNameId] = useState(user.className.id);
  if (user.className.id !== prevClassNameId) {
    setPrevClassNameId(user.className.id);
    setClassName(user.className.id);
  }

  useEffect(() => {
    if (menuOpen && user.className.id !== className && !saving) {
      const selectClass = async () => {
        setError(null);
        setSaving(true);
        try {
          await selectClassMutation({ id: className });
          await mutateUser();
          await queryClient.invalidateQueries();
          setError(null);
          if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
          setJustSaved(true);
          saveTimeoutRef.current = setTimeout(() => {
            setJustSaved(false);
            if (onClose) onClose();
          }, 0);
        } catch (err) {
          console.error("Error selecting game:", err);
          setError("Failed to switch game. Please try again.");
        } finally {
          setSaving(false);
        }
      };
      void selectClass();
    }
  }, [menuOpen, className, user.className.id, saving, selectClassMutation, onClose]);

  const updateClass = (event: SelectChangeEvent) => {
    setClassName(parseInt(event.target.value));
  };

  if (!menuOpen) return null;

  return (
    <Box
      sx={{
        bgcolor: "grey.200",
        minHeight: "80px",
        flexWrap: "wrap",
        width: "100%",
        display: "flex",
        alignItems: "center",
        px: 2,
        py: 1,
        gap: 2,
        borderBottom: 1,
        borderColor: "divider",
      }}
      role="region"
      aria-label="Game switcher"
    >
      {error && (
        <Alert severity="error" sx={{ width: "100%", mb: 1 }}>
          {error}
        </Alert>
      )}

      <FormControl sx={{ ml: 2, minWidth: 200 }}>
        <InputLabel>Game Select</InputLabel>
        <Select
          label="Game Select"
          value={className.toString()}
          onChange={updateClass}
          disabled={saving}
          aria-label="Select game"
        >
          {availableClasses
            ?.sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            .map((cls: ClassNameListResponse) => (
              <MenuItem key={cls.id} value={cls.id}>
                {cls.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 60, ml: 2 }}>
        {saving && <CircularProgress size={20} />}
        {!saving && justSaved && (
          <Typography sx={{ color: "success.main", fontWeight: 600 }}>
            Saved
          </Typography>
        )}
      </Box>
    </Box>
  );
}
